import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { PageTitle, StatusBadge } from "@/components/brand";
import TxTable, { TxDetails } from "@/components/TxTable";
import { Skeleton } from "@/components/ui/skeleton";

const Review = ({ tx, onDone }) => {
  const [reason, setReason] = useState("");
  if (tx.type !== "withdrawal" || !["pending", "processing"].includes(tx.status)) return null;
  const decide = async (decision) => {
    try { await api.post(`/admin/transactions/${tx.id}/decision`, { decision, reason }); toast.success(`Withdrawal ${decision}d`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 space-y-2 border-t border-cr-line pt-3">
      <p className="text-xs font-semibold uppercase text-cr-muted">Admin review</p>
      <input data-testid="admin-tx-reason" className="input-cr" placeholder="Decision note (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2">
        <button data-testid="admin-tx-approve" disabled={reason.length < 3} onClick={() => decide("approve")} className="btn-gold flex-1 text-xs">Approve</button>
        <button data-testid="admin-tx-reject" disabled={reason.length < 3} onClick={() => decide("reject")} className="btn-outline flex-1 text-xs text-cr-danger">Reject</button>
      </div>
    </div>
  );
};

const Webhooks = () => {
  const [ev, setEv] = useState(null);
  useEffect(() => { api.get("/admin/webhook-events").then((r) => setEv(r.data)); }, []);
  if (!ev) return <Skeleton className="h-40 bg-cr-elevated" />;
  return (
    <div className="overflow-x-auto rounded-xl border border-cr-line">
      <table data-testid="webhook-table" className="w-full text-sm">
        <thead className="bg-cr-elevated text-left text-xs text-cr-muted"><tr>{["Received", "Event ID", "Type", "Reference", "Signature", "Status", "Error"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>{ev.map((e) => <tr key={e.id} className="border-t border-cr-line text-xs"><td className="px-4 py-2">{dateTime(e.received_at)}</td><td className="px-4 py-2 font-mono">{e.event_id}</td><td className="px-4 py-2">{e.type}</td><td className="px-4 py-2 font-mono">{e.reference}</td><td className="px-4 py-2">{e.signature_valid ? "valid" : <span className="text-cr-danger">invalid</span>}</td><td className="px-4 py-2"><StatusBadge status={e.status === "processed" ? "completed" : e.status.startsWith("ignored") ? "cancelled" : e.status === "received" ? "pending" : "failed"} testid={`wh-${e.event_id}`} /> <span className="ml-1 text-cr-muted">{e.status}</span></td><td className="px-4 py-2 text-cr-danger">{e.error}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

export default function AdminTransactions() {
  const [tab, setTab] = useState("tx");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const load = () => api.get("/admin/transactions", { params: { type, status, q: q || undefined, page } }).then((r) => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [type, status, q, page]);
  return (
    <div data-testid="admin-transactions-page">
      <PageTitle title="Transaction Management" subtitle="Inspect transactions, review withdrawals and monitor payment callbacks." />
      <div className="mb-5 flex gap-6 border-b border-cr-line">
        {[["tx", "Transactions"], ["webhooks", "Payment callbacks"]].map(([k, l]) => <button key={k} data-testid={`admin-tx-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 pb-3 text-sm ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent"}`}>{l}</button>)}
      </div>
      {tab === "webhooks" ? <Webhooks /> : (
        <>
          <div className="mb-5 grid gap-3 md:grid-cols-[1fr_auto_auto]">
            <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" /><input data-testid="admin-tx-search" className="input-cr pl-10" placeholder="Reference, email, method" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>
            <select data-testid="admin-tx-type" className="input-cr" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>{["all", "deposit", "withdrawal", "adjustment"].map((x) => <option key={x}>{x}</option>)}</select>
            <select data-testid="admin-tx-status" className="input-cr" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>{["all", "pending", "completed", "failed", "cancelled", "rejected"].map((x) => <option key={x}>{x}</option>)}</select>
          </div>
          {!data ? <Skeleton className="h-40 bg-cr-elevated" /> : (
            <>
              <TxTable items={data.items} onView={setView} showUser testid="admin-tx-table" />
              <div className="mt-4 flex items-center justify-end gap-3 text-sm">
                <button data-testid="admin-tx-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-outline px-3 py-1 text-xs">Prev</button>
                <span>Page {data.page} / {data.pages}</span>
                <button data-testid="admin-tx-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="btn-outline px-3 py-1 text-xs">Next</button>
              </div>
            </>
          )}
        </>
      )}
      <TxDetails tx={view} onClose={() => setView(null)} extra={view && <Review tx={view} onDone={() => { setView(null); load(); }} />} />
    </div>
  );
}
